
import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SettingsContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
  autoProceed: boolean;
  toggleAutoProceed: () => void;
  enableLensFocus: boolean;
  toggleLensFocus: () => void;
  enableFeedback: boolean;
  toggleFeedback: () => void;
  enableRpgMode: boolean;
  toggleRpgMode: () => void;
  layoutDirection: 'ltr' | 'rtl';
  toggleLayoutDirection: () => void;
  tutorialCompleted: boolean;
  setTutorialCompleted: (completed: boolean) => void;
  saveSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface UserSettings {
  darkMode?: boolean;
  autoProceed?: boolean;
  enableLensFocus?: boolean;
  enableFeedback?: boolean;
  enableRpgMode?: boolean;
  layoutDirection?: 'ltr' | 'rtl';
  tutorialCompleted?: boolean;
}

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Default settings
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const [autoProceed, setAutoProceed] = useState<boolean>(true);
  const [enableLensFocus, setEnableLensFocus] = useState<boolean>(true);
  const [enableFeedback, setEnableFeedback] = useState<boolean>(true);
  const [enableRpgMode, setEnableRpgMode] = useState<boolean>(false);
  const [layoutDirection, setLayoutDirection] = useState<'ltr' | 'rtl'>('ltr');
  const [tutorialCompleted, setTutorialCompletedState] = useState<boolean>(false);

  // Load settings from localStorage immediately on mount
  useEffect(() => {
    console.log("Settings: Loading from localStorage");
    const savedSettings = localStorage.getItem('unstuckSettings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings) as UserSettings & { enableCharacterSheet?: boolean };
        setDarkMode(parsedSettings.darkMode ?? false);
        setAutoProceed(parsedSettings.autoProceed ?? true);
        setEnableLensFocus(parsedSettings.enableLensFocus ?? true);
        setEnableFeedback(parsedSettings.enableFeedback ?? true);
        // Handle legacy enableCharacterSheet setting
        const legacyCharacterSheet = parsedSettings.enableCharacterSheet ?? false;
        setEnableRpgMode(parsedSettings.enableRpgMode ?? legacyCharacterSheet ?? false);
        setLayoutDirection(parsedSettings.layoutDirection ?? 'ltr');
        setTutorialCompletedState(parsedSettings.tutorialCompleted ?? false);
        console.log("Settings: Loaded from localStorage", parsedSettings);
      } catch (error) {
        console.error("Error parsing saved settings:", error);
      }
    }
  }, []);

  // Save to localStorage whenever settings change
  useEffect(() => {
    console.log("Settings: Saving to localStorage");
    const settingsToSave: UserSettings = {
      darkMode,
      autoProceed,
      enableLensFocus,
      enableFeedback,
      enableRpgMode,
      layoutDirection,
      tutorialCompleted
    };
    localStorage.setItem('unstuckSettings', JSON.stringify(settingsToSave));
  }, [darkMode, autoProceed, enableLensFocus, enableFeedback, enableRpgMode, layoutDirection, tutorialCompleted]);

  const toggleDarkMode = () => setDarkMode(!darkMode);
  const toggleAutoProceed = () => setAutoProceed(!autoProceed);
  const toggleLensFocus = () => setEnableLensFocus(!enableLensFocus);
  const toggleFeedback = () => setEnableFeedback(!enableFeedback);
  const toggleRpgMode = () => setEnableRpgMode(!enableRpgMode);
  const toggleLayoutDirection = () => setLayoutDirection(layoutDirection === 'ltr' ? 'rtl' : 'ltr');
  
  const setTutorialCompleted = (completed: boolean) => {
    setTutorialCompletedState(completed);
  };

  // Separate function to sync settings to database when called
  const saveSettings = async () => {
    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        console.log("Settings: No user session, skipping database sync");
        return;
      }

      console.log("Settings: Syncing to database");
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: session.user.id,
          dark_mode: darkMode,
          auto_proceed: autoProceed,
          enable_lens_focus: enableLensFocus,
          enable_feedback: enableFeedback,
          enable_rpg_mode: enableRpgMode,
          layout_direction: layoutDirection,
          tutorial_completed: tutorialCompleted,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });
        
      if (error) {
        console.error("Error saving settings to database:", error);
      } else {
        console.log("Settings: Successfully saved to database");
      }
    } catch (error) {
      console.error("Error in saveSettings:", error);
    }
  };

  return (
    <SettingsContext.Provider 
      value={{ 
        darkMode, 
        toggleDarkMode, 
        autoProceed, 
        toggleAutoProceed,
        enableLensFocus,
        toggleLensFocus,
        enableFeedback,
        toggleFeedback,
        enableRpgMode,
        toggleRpgMode,
        layoutDirection,
        toggleLayoutDirection,
        tutorialCompleted,
        setTutorialCompleted,
        saveSettings
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};
